import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AgCharts } from "ag-charts-react";

import { equipmentsQuery } from "../../api";
import { LoadingContainer } from "../../common/loaders/loadingcontainer";
import { localized } from "../../common/utils";

export function EquipmentsOrdered() {
  const { t } = useTranslation();
  const { data, error, isLoading } = useQuery(equipmentsQuery);

  if (isLoading) return <LoadingContainer />;
  if (error) return error.message;

  const ordered = data.data.filter(
    (x) =>
      (x.orderDate || x.dueDate) &&
      !x.deliveryDate &&
      !x.returningDate &&
      !x.returnDate,
  );
  if (ordered.length === 0) return <div>({t("EquipmentsNoOrdered")})</div>;

  return (
    ordered.length && (
      <div>
        <h4 style={{ color: "var(--heeros-blue)" }}>
          {t("EquipmentsOrdered")}
        </h4>
        {ordered.map((x) => (
          <Row key={x.id}>
            <Col>{x.personName}</Col>
            <Col>
              {x.equipmentCodeItem?.description ||
                x.equipmentCodeItem?.codeValue}
            </Col>
            <Col>{x.code}</Col>
            <Col>
              {new Date(x.orderDate || x.dueDate).toLocaleDateString(
                localized.resolvedLanguage,
              )}
            </Col>
          </Row>
        ))}
      </div>
    )
  );
}

export function EquipmentsReturning() {
  const { t } = useTranslation();
  const { data, error, isLoading } = useQuery(equipmentsQuery);

  if (isLoading) return <LoadingContainer />;
  if (error) return error.message;

  const returning = data.data.filter((x) => x.returningDate && !x.returnDate);
  if (returning.length === 0) return <div>({t("EquipmentsNoReturning")})</div>;

  return (
    returning.length && (
      <div>
        <h4 style={{ color: "var(--heeros-blue)" }}>
          {t("EquipmentsReturning")}
        </h4>
        {returning.map((x) => (
          <Row key={x.id}>
            <Col>{x.personName}</Col>
            <Col>
              {x.equipmentCodeItem?.description ||
                x.equipmentCodeItem?.codeValue}
            </Col>
            <Col>{x.code}</Col>
            <Col>
              {new Date(x.returningDate).toLocaleDateString(
                localized.resolvedLanguage,
              )}
            </Col>
          </Row>
        ))}
      </div>
    )
  );
}

export function EquipmentCount() {
  const { t } = useTranslation();
  const { data, error, isLoading } = useQuery(equipmentsQuery);

  if (isLoading) return <LoadingContainer />;
  if (error) return error.message;

  const counts = {};
  for (const equipment of data.data) {
    if (equipment.deliveryDate && !equipment.returnDate) {
      const key =
        equipment.equipmentCodeItem?.description ||
        equipment.equipmentCodeItem?.codeValue ||
        equipment.code ||
        t("NoInfo");
      counts[key] = (counts[key] || 0) + 1;
    }
  }

  const pieChartData = [];
  for (const [key, value] of Object.entries(counts)) {
    pieChartData.push({ key, value });
  }

  /** @type {import("ag-grid-enterprise").AgChartOptions} */
  const pieChartOptions = {
    data: pieChartData,
    series: [
      {
        type: "pie",
        angleKey: "value",
        calloutLabelKey: "key", // outside the sectors
        sectorLabelKey: "value", // inside the sectors
        sectorLabel: {
          color: "white",
          fontWeight: "bold",
        },
        tooltip: {
          renderer: (params) => ({
            title: params.datum.value,
            data: []
          }),
        },
        fills: [
          "#00B8E4",
          "#6F42C1",
          "#003A78",
          "#08384F",
          "#B7A0E0",
          "#80DBF1",
        ], // Changed order
      },
    ],
    legend: {
      enabled: false,
    },
    locale: localized.agChartsLocale,
  };

  return data ? (
    <>
      <h4 style={{ color: "var(--heeros-blue)" }}>{t("EquipmentsInUse")}</h4>

      <div>
        <AgCharts options={pieChartOptions} />
      </div>
    </>
  ) : (
    <LoadingContainer />
  );
}
